/**
 * Session Replay privacy selectors (MAK-666).
 *
 * Replays run with Sentry's privacy defaults ON — `maskAllText`, `maskAllInputs`
 * and `blockAllMedia` — so everything is masked unless it is listed here. That
 * ordering is deliberate: a new page that renders customer data is masked
 * automatically, rather than leaking until someone remembers to annotate it.
 *
 * Only add a selector here if it can NEVER render customer data. When in doubt,
 * leave it masked — an unreadable replay is a much cheaper problem than a
 * shipping address in a third-party log store.
 */

/**
 * Chrome that is safe to read: navigation, controls, headings, column headers
 * and form labels. Deliberately NOT included: `td` (row data), any input value,
 * and the components listed in DO_NOT_UNMASK below.
 *
 * `label`/`label *` is safe in this codebase because labels reference their
 * control via `htmlFor` and are rendered as siblings, never as wrappers around
 * an input. If that pattern ever changes, drop the descendant selector — a
 * label wrapping an input would unmask the value typed into it.
 */
export const SENTRY_REPLAY_UNMASK = [
  'nav',
  'nav *',
  'button',
  'button *',
  '[role="tab"]',
  '[role="tab"] *',
  'h1',
  'h2',
  'h3',
  'h4',
  'th',
  'label',
  'label *',
  // Left menu navigation links. Targets the anchors specifically rather than
  // the whole sidebar: `.left-menu-navbar` also contains CompanyProfile and
  // CompanyPicker, which render company names and the list of other companies
  // the user can switch to. Those stay masked.
  '.left-menu-navbar a',
  '.left-menu-navbar a *',
  // Opt-in hook, applied at the point the value is rendered. Emotion class
  // names are hashed and change between builds, so components cannot be
  // targeted by selector — the attribute has to go on the element itself.
  //
  // Currently carries trading-partner and catalogue names, which are business
  // identifiers rather than consumer PII, and are needed to tell one row from
  // another when reading a replay:
  //   - orders list     supplier / buyer company name
  //   - invoices list   partner company name
  //   - products list   product title
  //   - suppliers list  supplier company name
  //
  // Do NOT extend this to end-consumer data: shipping names, addresses,
  // customer emails and phone numbers stay masked.
  '[data-sentry-unmask]',
  '[data-sentry-unmask] *',
];

/**
 * Media is blocked by default (`blockAllMedia`). Unblock only decorative assets
 * that cannot contain customer data — never product imagery, uploaded files or
 * document previews.
 */
export const SENTRY_REPLAY_UNBLOCK = ['[data-sentry-unblock]'];

/**
 * Components that must never be unmasked, recorded here so the reasoning
 * survives. Referenced by the spec; not consumed at runtime.
 *
 * - molecules/Body          generic body text, used to render data everywhere
 * - molecules/StreetAddress addresses
 * - molecules/Input|Select|Checkbox|RadioButton|Toggle   form values
 * - organisms/CompanyProfile, MetafieldsEditor, RichTextEditor
 * - organisms/SearchResults, Timeline, PendingInvite
 * - species/CompanyPicker   company names
 */
